'use client';
/* eslint-disable max-len */
import {
  Dialog,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';

import classNames from 'classnames';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
  Ads,
  Alert,
  Button,
  Close,
  FreeDeliveryAlert,
  Icon,
  IconSvg,
  Link,
  useAuth,
  useData,
} from '@/components';
import {
  useAnalytics,
  useEcomStoreSelector,
  useUiStoreSelector,
  useUpdateTreezCart,
} from '@/data';
import {
  useCurrentMinimumAmount,
  useFeaturedFlag,
  useIsServerSide,
  useSelectedStore,
  useSummary,
} from '@/hooks';
import { AdsSlice, TreezSpecial } from '@/types';
import {
  checkOrderForCustomDeliveryFee,
  currencyFormat,
  getTaxValue,
  mapLineItems,
} from '@/utils';

import CartBody from './CartBody';
import { CartShoppingInfo } from './CartShoppingInfo';
import Discount from './Discount';
import { SummaryTotalSkeleton } from './SummaryTotalSkeleton';

import style from './cartnavigation.module.scss';

const ALLOW_GUEST_CHECKOUT =
  process.env.NEXT_PUBLIC_ALLOW_GUEST_CHECKOUT === 'true';

const GO_TO_CHECKOUT_BTN_LABEL = 'Proceed to checkout';

const CartNavigation: FC = () => {
  const { goToSignIn, goToCheckout, isAuth } = useAuth();
  const { isServerSide } = useIsServerSide();
  const { isTaxAppliedMessage: displayPostTax } = useFeaturedFlag();

  const {
    berbix_verified,
    line_items,
    treez_store_name,
    delivery_details,
    delivery_address,
    entity_id,
    treez_pay_ticket_ids_by_store,
    total_service_fee,
    total_delivery,
    setState: setStoredState,
  } = useEcomStoreSelector([
    'berbix_verified',
    'treez_store_name',
    'line_items',
    'delivery_details',
    'delivery_address',
    'entity_id',
    'treez_pay_ticket_ids_by_store',
    'total_service_fee',
    'total_delivery',
  ]);

  const { is_cart_open, setState } = useUiStoreSelector(['is_cart_open']);
  const { apps, shoppingCart } = useData();
  const {
    serverSubtotal,
    preDiscountSubtotal,
    subtotal,
    totalGrams,
    tax,
    discounts,
    total,
  } = useSummary();
  const { currentMinAmount, delivery_schedules } = useCurrentMinimumAmount();
  const { store } = useSelectedStore();

  const isBerbixAppEnabled = useMemo(
    () => !!apps?.find(({ handler }) => handler === 'berbix'),
    [apps],
  );
  const needsBerbixValidation = useMemo(
    () => isBerbixAppEnabled && delivery_details && !berbix_verified,
    [delivery_details, berbix_verified],
  );
  const { measureViewCartGA4, measureSurfsideAction } = useAnalytics();

  // local state
  const [loading, setLoading] = useState(false);

  const [failedToLoadCartTotals, setFailedToLoadCartTotals] = useState<{
    message: string | undefined;
    status: boolean;
  }>({
    message: undefined,
    status: false,
  });

  const insufficientSellableQuantityError =
    failedToLoadCartTotals?.status &&
    failedToLoadCartTotals?.message?.includes('exceeds the sellable quantity');

  const deliveryMinimumError =
    failedToLoadCartTotals?.status &&
    failedToLoadCartTotals?.message?.includes('does not meet') &&
    failedToLoadCartTotals?.message?.includes('minimum for delivery');

  const [specials, setSpecials] = useState<TreezSpecial[] | undefined>(
    undefined,
  );
  const [checkoutValidation, setCheckoutValidation] = useState({
    disabled: false,
    label: GO_TO_CHECKOUT_BTN_LABEL,
  });
  const firstUpdateTreezCart = useRef(true);

  const heading = shoppingCart?.heading ?? 'Shopping cart';
  const buttonColor = shoppingCart?.button_color ?? 'primary';
  const message = shoppingCart?.free_delivery_message;
  const messageCompleted = shoppingCart?.free_delivery_activation_message;
  const ad = shoppingCart?.slices?.find((a: AdsSlice) => a?.primary?.active);

  // this update treez cart is only to calculate the totals
  // and display it in the cart

  const order = {
    entity_id: entity_id,
    email: '', // in the future we will have the email here so we can also display group discounts at the cart level
    line_items: mapLineItems(line_items),
    provider_store_full_address: store?.full_address,
    provider_store_id: store?.shortName,
    provider_store_name: store?.name,
    provider_store_phone_number: store?.phone,
    delivery_details,
    delivery_address,
    treez_pay_ticket_ids_by_store: treez_pay_ticket_ids_by_store,
  };

  checkOrderForCustomDeliveryFee({ delivery_schedules }, order, store);

  const {
    updateCart,
    result: { loading: isFetchingTotals },
  } = useUpdateTreezCart({
    variables: {
      input: {
        order: order,
      },
    },
    onCompleted: data => {
      const res = data?.updateTreezCart;
      if (!res) {
        return;
      }
      firstUpdateTreezCart.current = false;

      const { status, specials, order, error } = res;

      if (!status) {
        setFailedToLoadCartTotals({ message: error?.message, status: true });
        return;
      }

      // set the specials on the local state
      setSpecials(specials as any);

      // persist the order total values on the local state
      setStoredState({
        entity_id: order?.entity_id as string,
        total_discounts: order?.total_discounts as number,
        total_tax: order?.total_tax as number,
        total_price: order?.total_price as number,
        subtotal_price: order?.subtotal_price as number,
        total_delivery: order?.total_delivery ?? 0,
        total_service_fee: order?.total_service_fee ?? 0,

        //treez order
        treez_order_id: order?.treez_order_id,
        treez_order_number: order?.treez_order_number,
        treez_pay_ticket_ids_by_store:
          order?.treez_pay_ticket_ids_by_store ?? [],
      });

      setFailedToLoadCartTotals({ message: undefined, status: false });
    },
    onError(error) {
      // eslint-disable-next-line no-console
      console.error(error?.message);
      setFailedToLoadCartTotals({ message: error?.message, status: true });
    },
  });

  // validate the delivery minimum amount
  useEffect(() => {
    if (!treez_store_name) {
      return;
    }

    if (delivery_details?.pickup) {
      setCheckoutValidation({
        disabled: false,
        label: GO_TO_CHECKOUT_BTN_LABEL,
      });
      return;
    }

    if (currentMinAmount === 0) {
      setCheckoutValidation({
        disabled: false,
        label: GO_TO_CHECKOUT_BTN_LABEL,
      });
      return;
    }

    const minimumTotal = store?.delivery_minimum_total ? total : subtotal;
    const minimumTotalLabel = store?.delivery_minimum_total
      ? 'Grand Total'
      : 'Subtotal';
    const underCurrentMinAmount = currentMinAmount > minimumTotal;

    setCheckoutValidation({
      disabled: underCurrentMinAmount,
      label: underCurrentMinAmount
        ? `$${currencyFormat(
            currentMinAmount,
          )} ${minimumTotalLabel} Min. For Delivery`
        : GO_TO_CHECKOUT_BTN_LABEL,
    });
  }, [delivery_details, subtotal, treez_store_name, currentMinAmount, total]);

  // Go to checkout button event
  const handleProceed = async () => {
    if (!ALLOW_GUEST_CHECKOUT && (!isAuth || needsBerbixValidation)) {
      setState({ is_cart_open: false });
      setStoredState({ checkout_identity_verification: true });
      await goToSignIn();
      return;
    }

    setLoading(true);

    await goToCheckout();
    setState({ is_cart_open: false });
    setLoading(false);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setState({ is_cart_open: false });
      }
    };

    if (is_cart_open) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [is_cart_open, setState]);

  useEffect(() => {
    if (is_cart_open && line_items?.length) {
      measureViewCartGA4(line_items);
      measureSurfsideAction(line_items, 'cart');
    }
  }, [is_cart_open]);

  useEffect(() => {
    if (!is_cart_open) {
      if (firstUpdateTreezCart.current && line_items?.length) {
        firstUpdateTreezCart.current = false;
        updateCart();
      }
      return;
    }
    const timeout = setTimeout(() => {
      if (line_items?.length) {
        updateCart();
      }
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [is_cart_open, line_items]);

  if (isServerSide) return null;

  const displayTotals =
    line_items?.length > 0 &&
    !isFetchingTotals &&
    !failedToLoadCartTotals?.status;

  const displayTaxDiscountAtCheckout =
    line_items?.length > 0 &&
    !isFetchingTotals &&
    failedToLoadCartTotals?.status;

  const freeAfter = delivery_schedules?.[0]?.free_after ?? 0;

  return (
    <Dialog
      aria-labelledby="cart-title"
      aria-modal="true"
      className={classNames(style.cart_nav, {
        [style.cart_nav__open]: is_cart_open,
      })}
      onClose={() => setState({ is_cart_open: false })}
      open={is_cart_open}
      role="dialog"
      static
      tabIndex={-1}
    >
      <div className={style.cart_nav__container}>
        <div className={style.cart_nav__body}>
          <div className={style.cart__header}>
            <div className={style.cart__shopping}>
              <Link
                aria-label="Return to homepage"
                href="/"
                onClick={() => setState({ is_cart_open: false })}
              >
                <h2 className={style.cart__shopping_link} id="cart-title">
                  {heading}
                </h2>
              </Link>
              <Close onClick={() => setState({ is_cart_open: false })} />
            </div>
            <CartShoppingInfo />
          </div>
          {(insufficientSellableQuantityError || deliveryMinimumError) && (
            <Alert error={true} isOpen={true} noAction={true}>
              <span>{failedToLoadCartTotals?.message}</span>
            </Alert>
          )}
          {line_items?.length == 0 && (
            <div className={style.cart__empty}>
              {/* <Icon className={style.empty_cart_icon} name="empty-cart-icon" /> */}
              <figure className={style.empty_cart_icon}>
                <IconSvg name="empty-shopping-cart" />
              </figure>
              <p style={{ width: '100%' }}>
                Your bag is empty <br /> Let’s go shopping now!
              </p>
            </div>
          )}
          {line_items?.length > 0 && <CartBody products={line_items} />}
        </div>
        {ad && <Ads {...ad} />}
        <div className={style.cart__footer}>
          <div className={style.footer__current_shopping}>
            <div className={style.footer__grams_container}>
              <span className={style.heading}>
                Total Weight{' '}
                <Popover className={style.footer__help}>
                  <PopoverButton aria-label="Show info">
                    <Icon name="help" />
                  </PopoverButton>
                  <PopoverPanel className={style.footer__help_body}>
                    <div className={style.peak}></div>
                    Total Weight is calculated only for products measured in
                    grams—like flower, pre-rolls, cartridges, and extracts.
                    Items such as edibles, beverages, tinctures, and topicals
                    are typically excluded. This helps ensure compliance with
                    legal purchase limits.
                  </PopoverPanel>
                </Popover>
              </span>
              <span className={style.grams}>{totalGrams?.toFixed(2)} G</span>
            </div>

            {/* Subtotal */}
            <div
              className={classNames(style.footer__grams_container, {
                [style.footer__grams_container_subtotal]:
                  isFetchingTotals || failedToLoadCartTotals?.status,
              })}
            >
              <span className={style.heading}>Subtotal</span>
              <span className={style.grams}>
                $
                {`${
                  displayTotals
                    ? currencyFormat(serverSubtotal)
                    : currencyFormat(preDiscountSubtotal)
                }`}
              </span>
            </div>

            {/* Loading Skeleton */}
            {isFetchingTotals && <SummaryTotalSkeleton />}

            {displayTotals && (
              <>
                {/* Discounts */}
                {discounts > 0 && (
                  <Discount discounts={discounts} specials={specials} />
                )}

                {/* Fee */}
                {delivery_details?.pickup ? (
                  <p className={style.fee}>
                    Pickup fee
                    <span className={style.total_fee}>
                      {total_service_fee
                        ? `$${currencyFormat(total_service_fee)}`
                        : 'Free'}
                    </span>
                  </p>
                ) : (
                  <p className={style.fee}>
                    Delivery fee
                    <span className={style.total_fee}>
                      {total_delivery
                        ? `$${currencyFormat(total_delivery)}`
                        : 'Free'}
                    </span>
                  </p>
                )}

                {/* Tax */}
                <div className={style.footer__grams_container}>
                  <span className={style.heading}>Taxes</span>

                  <span className={style.grams}>
                    {getTaxValue(displayPostTax, tax)}
                  </span>
                </div>

                {/* Totals */}
                <p>
                  Total payment{' '}
                  <span className={style.subtotal}>
                    ${currencyFormat(total)}
                  </span>
                </p>
              </>
            )}

            {displayTaxDiscountAtCheckout && (
              <small className={style.tax_info}>
                (tax and discounts applied at check-out)
              </small>
            )}

            {delivery_details?.delivery && (
              <FreeDeliveryAlert
                classname={style.delivery_alert}
                message={message}
                messageCompleted={messageCompleted}
                minDeliveryAmount={freeAfter}
                totalAmount={subtotal}
              />
            )}

            <Button
              aria-disabled={
                line_items?.length == 0 || checkoutValidation.disabled
              }
              className={style.proceed}
              color={buttonColor}
              disabled={
                line_items?.length == 0 ||
                checkoutValidation.disabled ||
                insufficientSellableQuantityError ||
                isFetchingTotals
              }
              loading={loading}
              onClick={handleProceed}
              size="lg"
            >
              <div className={style.proceed_label}>
                {checkoutValidation.label}
                <IconSvg name="arrow-right" />
              </div>
            </Button>
          </div>
        </div>
      </div>
      <div
        className={style.cart_nav__overlap}
        onClick={() => setState({ is_cart_open: false })}
        role="button"
      />
    </Dialog>
  );
};

export default CartNavigation;
